import React, { useState, useRef, useEffect } from "react";
import { Bot, X, Send, Loader2, Sparkles, Lightbulb, Plus, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { BACKEND_URL, API_V1} from "../config/api";

const API = `${BACKEND_URL}/api/v1/admin`;

const QUICK_TYPES = [
  { type: "task", label: "Nový úkol", emoji: "task" },
  { type: "customer", label: "Nový zákazník", emoji: "cust" },
  { type: "event", label: "Nová událost", emoji: "event" },
];

export default function FloatingAiButton() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Ahoj! Jsem AI asistent. Jak vám mohu pomoci?", ts: Date.now() }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`fab-${Date.now()}`);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Hide on the AI agent page itself
  if (location.pathname === "/admin/ai-agent") return null;

  const scrollDown = () => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const addMsg = (role, content) => {
    setMessages(prev => [...prev, { role, content, ts: Date.now() }]);
    scrollDown();
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    addMsg("user", text);
    setLoading(true);
    try {
      const res = await axios.post(`${API_V1}/ai-agent/chat`, { message: text, session_id: sessionId });
      addMsg("assistant", res.data.response);
    } catch (e) {
      addMsg("assistant", `Chyba: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
      scrollDown();
    }
  };

  const handleQuickCreate = async (type) => {
    addMsg("user", `Rychle vytvořit: ${type}`);
    setLoading(true);
    try {
      const defaults = {
        task: { title: "Nový úkol" },
        customer: { name: "Nový zákazník" },
        event: { title: "Nová událost", start_date: new Date().toISOString().split("T")[0] },
      };
      const res = await axios.post(`${API_V1}/ai-agent/quick-create`, { type, fields: defaults[type] || {} });
      if (res.data.ok) {
        addMsg("assistant", `Vytvořeno: ${res.data.label}${res.data.number ? ` (${res.data.number})` : ""}. Chcete otevřít detail?`);
        toast.success(`${res.data.label} vytvořen`);
      }
    } catch (e) {
      addMsg("assistant", `Chyba: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
      scrollDown();
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 200); }}
          className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg shadow-violet-200 hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
          data-testid="floating-ai-btn"
        >
          <Bot className="w-6 h-6 group-hover:scale-110 transition-transform" />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white animate-pulse" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
          data-testid="floating-ai-panel">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-indigo-600 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-white" />
              <div>
                <span className="text-white text-sm font-semibold">AI Asistent</span>
                <span className="block text-violet-200 text-[10px]">Claude Sonnet</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => navigate("/admin/ai-agent")}
                className="text-violet-200 hover:text-white text-[10px] px-2 py-1 rounded hover:bg-white/10 transition-colors"
                data-testid="floating-ai-fullscreen">
                Celá obrazovka
              </button>
              <button onClick={() => setOpen(false)}
                className="text-violet-200 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-colors"
                data-testid="floating-ai-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Quick actions */}
          <div className="px-3 py-2 border-b border-slate-100 flex gap-1.5 flex-shrink-0">
            {QUICK_TYPES.map(q => (
              <button key={q.type} onClick={() => handleQuickCreate(q.type)}
                className="text-[10px] px-2 py-1 rounded-lg bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors font-medium"
                data-testid={`fab-quick-${q.type}`}>
                <Plus className="w-3 h-3 inline mr-0.5" />{q.label}
              </button>
            ))}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 min-h-0">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-xs leading-relaxed whitespace-pre-line ${
                  msg.role === "user"
                    ? "bg-violet-600 text-white rounded-br-sm"
                    : "bg-slate-100 text-slate-700 rounded-bl-sm"
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 rounded-xl rounded-bl-sm px-3 py-2 flex gap-1">
                  {[0,1,2].map(i => (
                    <span key={i} className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"
                      style={{ animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-2 border-t border-slate-100 flex-shrink-0">
            <div className="flex items-center gap-2 bg-slate-50 rounded-xl border border-slate-200 focus-within:border-violet-400 px-3 py-1.5">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleSend(); } }}
                placeholder="Napište zprávu..."
                className="flex-1 bg-transparent text-xs outline-none placeholder-slate-400"
                data-testid="floating-ai-input"
              />
              <button onClick={handleSend} disabled={loading || !input.trim()}
                className="w-7 h-7 rounded-lg bg-violet-600 hover:bg-violet-700 text-white flex items-center justify-center disabled:opacity-40 transition-colors flex-shrink-0"
                data-testid="floating-ai-send">
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
